import styled from 'styled-components';
import { Tabs } from '@mantine/core';
import { PALETTE } from '@dealstack/shared/lib/common/business/stampDesign';

import {
	SIGNATURE_DROP_LINE_CLASS,
	SIGNATURE_PAIR_ATTRIBUTE,
} from './extensions/signaturePairHighlight';
import { SIGNATURE_STAMP_ATTRIBUTE } from './extensions/signaturePairing';
import { RULE_BLOCK_CLASS, RULE_LABEL_ATTRIBUTE } from './extensions/ruleDecorations';
import { SELECTED_CELL_CLASS } from './extensions/tableEditing';

import { DataPointRow } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/bindPanel.styled';

/** Matches `LIST_INDENT_STEP_MM` in `flowRender`, so a marker sits where the PDF puts it. */
const LIST_INDENT_STEP_MM = 8;

/**
 * A page-shaped surface. Width in millimetres so it matches what the renderer will produce.
 *
 * The base font comes from the DOCUMENT, not from a constant: a letter set in Arial has to look
 * like one here, or the author is checking their edits against a page that is not the page they
 * will generate. `font-family: inherit` appears more than once below for the same reason — the
 * app's own reset sets `div`/`span` to the UI face, and a type selector beats an inherited
 * value, so anything the editor renders inside a wrapper element needs the inheritance restored.
 */
export const EditorSurface = styled.div<{
	$pageWidthMm: number;
	$marginMm: number;
	$baseFontFamily: string;
	$baseFontSizePt: number;
}>`
	background: #ffffff;
	width: ${({ $pageWidthMm }) => $pageWidthMm}mm;
	max-width: 100%;
	min-height: 40mm;
	padding: ${({ $marginMm }) => $marginMm}mm;
	margin: 0 auto;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);

	.ProseMirror {
		outline: none;
		font-family: ${({ $baseFontFamily }) => $baseFontFamily};
		font-size: ${({ $baseFontSizePt }) => $baseFontSizePt}pt;
		line-height: 1.15;
	}

	/* Spacing comes from each paragraph's own attributes; this is only the fallback for a
	   paragraph that states none. */
	.ProseMirror p {
		margin: 0 0 2mm;
	}

	/*
	 * Lists.
	 *
	 * The app's reset sets "ol, ul { list-style: none }" and zeroes their padding, so without
	 * this every list in the document renders as flush prose with no marker at all. The marker
	 * is drawn as a ::before in a hanging gutter rather than left to list-style-type, for two
	 * reasons: the gutter is what the renderer does (LIST_INDENT_STEP_MM), and a template like
	 * "(%1)" cannot be expressed as a list-style-type — the browser would draw "a." instead.
	 */
	.ProseMirror ul,
	.ProseMirror ol {
		list-style: none;
		margin: 0;
		padding-left: ${LIST_INDENT_STEP_MM}mm;
	}

	.ProseMirror li {
		position: relative;
	}

	.ProseMirror li::before {
		position: absolute;
		left: -${LIST_INDENT_STEP_MM}mm;
		width: ${LIST_INDENT_STEP_MM}mm;
		/* The marker belongs to the item's first line, whatever that line's own size is. */
		font-family: inherit;
	}

	.ProseMirror ul > li::before {
		content: var(--ds-list-marker, '\\2022');
	}

	/* counter(list-item) is the browser's own list counter, so it honours the ol start attribute
	   and renumbers as items are inserted — the whole reason a marker is never stored. */
	.ProseMirror ol > li::before {
		content: var(--ds-list-prefix, '') counter(list-item, decimal) var(--ds-list-suffix, '.');
	}

	.ProseMirror ol[data-numberformat='decimalZero'] > li::before {
		content: var(--ds-list-prefix, '') counter(list-item, decimal-leading-zero)
			var(--ds-list-suffix, '.');
	}

	.ProseMirror ol[data-numberformat='lowerLetter'] > li::before {
		content: var(--ds-list-prefix, '') counter(list-item, lower-alpha)
			var(--ds-list-suffix, '.');
	}

	.ProseMirror ol[data-numberformat='upperLetter'] > li::before {
		content: var(--ds-list-prefix, '') counter(list-item, upper-alpha)
			var(--ds-list-suffix, '.');
	}

	.ProseMirror ol[data-numberformat='lowerRoman'] > li::before {
		content: var(--ds-list-prefix, '') counter(list-item, lower-roman)
			var(--ds-list-suffix, '.');
	}

	.ProseMirror ol[data-numberformat='upperRoman'] > li::before {
		content: var(--ds-list-prefix, '') counter(list-item, upper-roman)
			var(--ds-list-suffix, '.');
	}

	/* A template naming its ancestors ("%1.%2") needs every level, not just this one. */
	.ProseMirror ol[style*='--ds-list-separator'] > li::before {
		content: var(--ds-list-prefix, '') counters(list-item, '.') var(--ds-list-suffix, '');
	}

	/*
	 * Italic, restored.
	 *
	 * The app's global reset lists em and i among the elements it flattens, setting
	 * "font-style: inherit" on them — so the mark applied and the <em> rendered, but the text
	 * stayed upright and both the toolbar button and Mod-i looked broken. Bold is unaffected
	 * because that same block does not touch font-weight, which is why only italic looked dead.
	 */
	.ProseMirror em,
	.ProseMirror i {
		font-style: italic;
	}

	/* Word highlighting. Authoring markup, not document content: it marks the placeholders an
	   analyst has to replace, which is exactly why the editor has to show it. */

	.ProseMirror mark {
		background-color: var(--ds-highlight, #fff2a8);
		color: inherit;
		padding: 0 0.05em;
	}

	/* TipTap's table node view wraps every table in a div, which the app's reset would
	   otherwise set to the UI font — the whole table then renders in a face the document
	   never mentions. */
	.ProseMirror .tableWrapper {
		font-family: inherit;
		overflow-x: auto;
	}

	.ProseMirror table {
		border-collapse: collapse;
		width: 100%;
		margin: 0 0 3mm;
		font-family: inherit;
	}

	/* The source's own ruling, fed in as custom properties by the table's attributes. A table
	   that rules nothing — a borderless execution block, say — gets a faint dashed guide
	   instead, so its cells stay findable without inventing a grid the document does not have. */
	.ProseMirror td,
	.ProseMirror th {
		position: relative;
		border: var(--ds-cell-border-width, 1px) dashed var(--ds-cell-border-color, #e3e7ea);
		padding: 1.5mm;
		vertical-align: top;
		font-family: inherit;
	}

	.ProseMirror table[data-borderwidthmm] td,
	.ProseMirror table[data-borderwidthmm] th {
		border-style: solid;
	}

	/* A selected cell. prosemirror-tables only adds the class, and TipTap hides the native
	   selection while cells are selected, so without this a drag across cells showed nothing. */
	.ProseMirror .${SELECTED_CELL_CLASS}::after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 2;
		background: rgba(35, 131, 226, 0.22);
		pointer-events: none;
	}

	/* The overlay already marks the whole cell; the native selection inside it would tint twice. */
	.ProseMirror .${SELECTED_CELL_CLASS} *::selection {
		background: transparent;
	}

	/* A field reads as one object, because that is what it is. */
	.ProseMirror .ds-field {
		display: inline-block;
		padding: 0 0.3em;
		border-radius: 0.25em;
		background: #e7f0fb;
		color: #1c4f8f;
		white-space: nowrap;
		cursor: default;
		font-family: inherit;
		font-size: inherit;
	}

	.ProseMirror .ds-field.ProseMirror-selectednode {
		outline: 2px solid #1c4f8f;
	}

	/* A name no data point backs is rendered as a BLANK in the generated document, so it must
	   not be able to pass for a live field: red, not another shade of the bound blue. Both rules
	   below sit after the bound ones deliberately — same specificity, so source order decides.
	   The ring is inset so marking a field cannot resize it and reflow the line it sits on. */
	.ProseMirror .ds-field--unbound {
		background: #fee7ea;
		color: #c20a23;
		box-shadow: inset 0 0 0 1px #f992a0;
	}

	.ProseMirror .ds-field--unbound.ProseMirror-selectednode {
		outline: 2px solid #c20a23;
	}

	/*
	 * Conditional content.
	 *
	 * Always drawn, never behind a toggle: a rule that changes the deed and cannot be seen is
	 * Word's field-code problem, where the logic is real and invisible unless you know a key
	 * combination. Violet on purpose — blue is a bound field and red is an unbound one, and a
	 * third meaning must not borrow either.
	 */
	.ProseMirror .${RULE_BLOCK_CLASS} {
		position: relative;
		border-left: 3px solid #b197fc;
		padding-left: 3mm;
		border-radius: 0 3px 3px 0;
		background: linear-gradient(90deg, rgba(103, 65, 217, 0.05), rgba(103, 65, 217, 0));
	}

	/* The label names the rule, on the first block of each run. Drawn from an attribute, so it
	   stays out of the document: it can never be typed into, copied, or rendered into a deed. */
	.ProseMirror .${RULE_BLOCK_CLASS}[${RULE_LABEL_ATTRIBUTE}]::before {
		content: attr(${RULE_LABEL_ATTRIBUTE});
		display: table;
		margin: 0 0 1mm;
		padding: 0 6px;
		border: 1px solid #b197fc;
		border-radius: 4px;
		background: #f3f0ff;
		color: #6741d9;
		font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
		font-size: 9pt;
		font-weight: 600;
		line-height: 1.6;
		letter-spacing: 0.01em;
		user-select: none;
	}

	/* A row cannot carry a block-level label, so it gets the gutter on its first cell only. */
	.ProseMirror tr.${RULE_BLOCK_CLASS} {
		border-left: 3px solid #b197fc;
	}

	.ProseMirror tr.${RULE_BLOCK_CLASS}::before {
		content: none;
	}

	/* A page break is real structure in the document, so it is drawn rather than left implicit. */
	.ProseMirror [data-pagebreakbefore='true'] {
		border-top: 1px dashed #adb5bd;
		padding-top: 3mm;
	}

	/* Where a dragged signature will land: against the edge of the STAMP, not of the row it
	   sits in, because the row spans the page and the stamp may be anywhere across it. */
	.ProseMirror [${SIGNATURE_PAIR_ATTRIBUTE}] [${SIGNATURE_STAMP_ATTRIBUTE}]::after {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		width: 4px;
		border-radius: 2px;
		background: ${PALETTE.brand};
	}

	.ProseMirror [${SIGNATURE_PAIR_ATTRIBUTE}='left'] [${SIGNATURE_STAMP_ATTRIBUTE}]::after {
		left: -8px;
	}

	.ProseMirror [${SIGNATURE_PAIR_ATTRIBUTE}='right'] [${SIGNATURE_STAMP_ATTRIBUTE}]::after {
		right: -8px;
	}

	/* Where a carried signature will land when it is NOT pairing with one already there.
	   ProseMirror's own drop cursor covers the palette's drag; this covers the pointer gesture,
	   which the browser knows nothing about. */
	.ProseMirror .${SIGNATURE_DROP_LINE_CLASS} {
		height: 2px;
		margin: 1mm 0;
		border-radius: 1px;
		background: ${PALETTE.brand};
	}
`;

/** Toolbar above, scrolling page below. */
export const EditorStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	min-height: 0;
	flex: 1;
`;

/**
 * The formatting bar, shaped like Word's Home ribbon: one row of grouped controls.
 *
 * `nowrap` with horizontal overflow rather than `wrap`: wrapping put every group on its own
 * line and turned a ribbon into a column six rows tall.
 */
export const ToolbarBar = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: nowrap;
	gap: 0.125rem;
	padding: 0.25rem 0.375rem;
	border: 1px solid #dee2e6;
	border-radius: 0.375rem;
	background: #ffffff;
	overflow-x: auto;
	flex-shrink: 0;
`;

export const ToolbarGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 0.125rem;
	flex-shrink: 0;
`;

/** A plain rule, not a Mantine Divider: a vertical Divider in a flex row has no height of its
 *  own and stretched the bar instead of separating it. */
export const ToolbarSeparator = styled.span`
	width: 1px;
	height: 1.25rem;
	margin: 0 0.3rem;
	background: #dee2e6;
	flex-shrink: 0;
`;

/**
 * A ribbon button.
 *
 * Styled rather than a Mantine ActionIcon so the contrast is stated here: the v7 grey ramp
 * renders a subtle icon button light enough to read as disabled, which is how the first
 * attempt at this bar looked.
 */
export const ToolbarButton = styled.button<{ $active?: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 1.75rem;
	height: 1.75rem;
	padding: 0;
	border: none;
	border-radius: 0.25rem;
	background: ${({ $active }) => ($active ? '#dbe7f6' : 'transparent')};
	color: ${({ $active }) => ($active ? '#1c4f8f' : '#1f2933')};
	cursor: pointer;
	flex-shrink: 0;

	&:hover:not(:disabled) {
		background: ${({ $active }) => ($active ? '#cfe0f5' : '#f1f3f5')};
	}

	&:disabled {
		color: #adb5bd;
		cursor: default;
	}
`;

/** A worded toolbar button, in the violet the rule gutter and labels use. */
export const ToolbarRuleButton = styled(ToolbarButton)<{ $primary?: boolean }>`
	width: auto;
	padding: 0 0.5rem;
	font-size: 0.8125rem;
	font-weight: 600;
	color: #5f3dc4;
	background: ${({ $primary }) => ($primary ? '#f3f0ff' : 'transparent')};

	&:hover:not(:disabled) {
		background: #e5dbff;
	}
`;

export const EditorScroll = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
	background: #f1f3f5;
	padding: 6mm 0;
`;

export const SuggestionPanel = styled.div`
	background: #ffffff;
	border: 1px solid #dee2e6;
	border-radius: 0.375rem;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14);
	max-height: 15rem;
	overflow-y: auto;
	min-width: 16rem;
	padding: 0.25rem;
`;

export const SuggestionRow = styled.button<{ $active: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.05rem;
	width: 100%;
	padding: 0.3rem 0.45rem;
	border: none;
	border-radius: 0.25rem;
	background: ${({ $active }) => ($active ? '#e7f0fb' : 'transparent')};
	cursor: pointer;
	text-align: left;
`;

/**
 * The catalogue. Matches the bind panel's `CatalogueScroller`, minus the scroll.
 *
 * It used to scroll itself, which pinned the heading, the security scope card and the search box
 * above it — on a 440px panel that left the list a sliver, and the pinned chrome was the part
 * you most wanted out of the way. `PaletteScroll` now moves the whole lot as one, so this grows
 * to its content rather than competing for the space.
 */
export const PaletteList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

/** The bind panel's row, which here inserts on click and can also be dragged into place. */
export const PaletteRow = styled(DataPointRow)`
	cursor: pointer;

	&:hover {
		border-color: ${({ theme }) => theme.v3.colors.blue[6]};
	}
`;

/**
 * Holds the editor in the DOM while something else is on screen.
 *
 * `display: none`, not an unmount. The editor reads its document once, on mount — feeding it
 * back in on every change would reset the selection mid-typing — so unmounting it to show a
 * preview and mounting it again on the way back re-read the STORED document and threw away
 * every unsaved edit, along with the undo history and the caret.
 */
export const EditorMountSlot = styled.div<{ $hidden: boolean }>`
	display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
	flex: 1;
	min-height: 0;
`;

/** 440px for the side panel, matching the PDF editor's split, so the two screens line up. */
export const EditorPaneSplit = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 440px;
	gap: 1rem;
	flex: 1;
	min-height: 0;
`;

export const EditorSidePanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-height: 0;
	overflow: hidden;
`;

/** The panel's tab bar. `flex-shrink: 0` so the scrolling palette under it gives way, not this. */
export const PaletteTabs = styled(Tabs)`
	flex-shrink: 0;
`;

/**
 * Everything below the tab bar, scrolling as one.
 *
 * The tabs are the only thing pinned, because they are the only thing you need in reach at all
 * times — switching surface is how you leave. The heading, the security scope card, the search
 * box and the list all move together: they were fixed chrome around a list that scrolled on its
 * own, and between them they took most of a 440px panel before a single data point showed.
 */
export const PaletteScroll = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

/** Equal halves, and one unbroken rule beneath both — see `PaletteTab` for why. */
export const PaletteTabsList = styled(Tabs.List)`
	flex-wrap: nowrap;
	gap: 0;
`;

/**
 * Half the bar each, the active half marked on the rule beneath it.
 *
 * The rule is drawn PER TAB rather than on the list, because the app's Mantine theme sets
 * `list: { borderBottom: 'none' }` and v7 applies theme `styles` as inline styles — nothing a
 * class here could win against. Every tab carries the same 2px border and only its colour
 * changes, so the bar reads as one rule with a coloured segment on it and nothing shifts when
 * the active tab changes. Weight carries the same signal for anyone who cannot see the colour.
 *
 * Sized by `grow` on the list, so the segment spans the whole half rather than sitting under
 * the label — which is what made a short underline read as a stray mark in a 440px panel.
 */
export const PaletteTab = styled(Tabs.Tab)`
	justify-content: center;
	font-weight: 400;
	font-variation-settings: 'wght' 400;

	&,
	&:hover {
		border-bottom: 2px solid ${({ theme }) => theme.v3.colors.gray[3]};
	}

	& + & {
		border-left: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	}

	&[data-active='true'],
	&[data-active='true']:hover {
		border-bottom-color: ${({ theme }) => theme.v3.colors.blue[6]};
		font-weight: 600;
		font-variation-settings: 'wght' 600;
	}
`;
